import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Stage, Layer, Image as KonvaImage, Group, Rect, Line, Text } from 'react-konva';
import type Konva from 'konva';
import { Button } from '@/components/ui/button';
import { exportMime, exportQuality, useHtmlImage, type ExportFormat } from './photoSlot';
import { NAME_FONT_FAMILY, type CardTemplate } from './templates';

/** Artist-name fitting: line spacing, inner padding, and the smallest font we'll shrink to. */
const NAME_LINE_HEIGHT = 1.05;
const NAME_PADDING = 6;
const NAME_MIN_FONT_SIZE = 14;

/** Editable state of a card. */
export interface CardValue {
  artistName: string;
  /** Show/programme name — only used by resident templates. */
  showName?: string;
  /** Photo as a data/blob/object URL, or null when none chosen yet. */
  photoSrc: string | null;
  /** Photo placement within the frame: top-left in logical px + uniform scale. */
  transform: { x: number; y: number; scale: number } | null;
}

/**
 * Fit text into a fixed panel: word-wrap at the design size first, then shrink
 * the font only if the wrapped lines still overflow the box (or a single word
 * is wider than it). Returns the font size to render at.
 */
function fitFontSize(
  ctx: CanvasRenderingContext2D,
  text: string,
  box: { width: number; height: number },
  baseFontSize: number,
  fontFamily: string,
): number {
  const maxW = box.width - NAME_PADDING * 2;
  const maxH = box.height - NAME_PADDING * 2;
  const words = text.split(/\s+/).filter(Boolean);
  const fits = (size: number) => {
    ctx.font = `400 ${size}px "${fontFamily}", sans-serif`;
    let lines = 1;
    let widest = 0;
    let cur = '';
    for (const w of words) {
      const test = cur ? `${cur} ${w}` : w;
      if (!cur || ctx.measureText(test).width <= maxW) {
        cur = test;
      } else {
        widest = Math.max(widest, ctx.measureText(cur).width);
        lines += 1;
        cur = w;
      }
    }
    widest = Math.max(widest, ctx.measureText(cur).width);
    return widest <= maxW && lines * size * NAME_LINE_HEIGHT <= maxH;
  };
  let size = baseFontSize;
  while (size > NAME_MIN_FONT_SIZE && !fits(size)) size -= 1;
  return size;
}

export interface CardEditorHandle {
  /** Render the card to a data URL at 2x the template's canvas size. */
  toDataURL: (format?: ExportFormat) => string | null;
  /** Render the card to a Blob at 2x. */
  toBlob: (format?: ExportFormat) => Promise<Blob | null>;
}

interface CardEditorProps {
  template: CardTemplate;
  value: CardValue;
  onChange: (next: CardValue) => void;
  /** Hide the "drop a photo" affordance chrome (e.g. read-only preview). */
  className?: string;
}

/** Uniform scale + centered offset that makes an image fully cover a frame. */
function coverTransform(
  imgW: number,
  imgH: number,
  frame: { x: number; y: number; width: number; height: number },
) {
  const scale = Math.max(frame.width / imgW, frame.height / imgH);
  return {
    scale,
    x: frame.x + (frame.width - imgW * scale) / 2,
    y: frame.y + (frame.height - imgH * scale) / 2,
  };
}

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

/**
 * Clamp a photo's top-left so the scaled image always fully covers the frame —
 * no navy/halftone ever peeks through inside the photo rectangle.
 */
function clampToCover(
  x: number,
  y: number,
  imgW: number,
  imgH: number,
  scale: number,
  frame: { x: number; y: number; width: number; height: number },
) {
  const dispW = imgW * scale;
  const dispH = imgH * scale;
  return {
    x: clamp(x, frame.x + frame.width - dispW, frame.x),
    y: clamp(y, frame.y + frame.height - dispH, frame.y),
  };
}

export const CardEditor = forwardRef<CardEditorHandle, CardEditorProps>(function CardEditor(
  { template, value, onChange, className },
  ref,
) {
  const stageRef = useRef<Konva.Stage>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { canvas } = template;

  const backImg = useHtmlImage(template.assets.back);
  const logoImg = useHtmlImage(template.assets.logo);
  const photoImg = useHtmlImage(value.photoSrc);

  const [fontReady, setFontReady] = useState(false);
  const [fit, setFit] = useState(0.4);

  // Fit the stage to the container width, capping height so it stays on screen.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const availW = el.clientWidth;
      const availH = Math.min(window.innerHeight * 0.72, 900);
      setFit(Math.min(availW / canvas.width, availH / canvas.height));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [canvas.width, canvas.height]);

  // Load the custom name font, then trigger a redraw so Konva re-measures text.
  useEffect(() => {
    let active = true;
    const done = () => active && setFontReady(true);
    if (typeof document !== 'undefined' && 'fonts' in document) {
      document.fonts
        .load(`400 48px "${NAME_FONT_FAMILY}"`)
        .then(() => document.fonts.ready)
        .then(done)
        .catch(done);
    } else {
      done();
    }
    return () => {
      active = false;
    };
  }, []);

  // Cover-fit the photo when it first loads (transform null) or when the frame
  // changes to a different size (e.g. switching platform) and there's no
  // placement — the caller resets transform to null on such switches.
  const pf0 = template.photoFrame;
  useEffect(() => {
    if (photoImg && !value.transform) {
      onChange({
        ...value,
        transform: coverTransform(photoImg.width, photoImg.height, pf0),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoImg, pf0.x, pf0.y, pf0.width, pf0.height]);

  const handleDragEnd = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>) => {
      if (!value.transform) return;
      onChange({
        ...value,
        transform: { ...value.transform, x: e.target.x(), y: e.target.y() },
      });
    },
    [value, onChange],
  );

  // Zoom around the frame's centre so the subject stays put.
  const handleZoom = useCallback(
    (nextScale: number) => {
      if (!value.transform || !photoImg) return;
      const t = value.transform;
      const frame = template.photoFrame;
      const cx = frame.x + frame.width / 2;
      const cy = frame.y + frame.height / 2;
      // Keep the point currently under the frame centre fixed.
      const imgPtX = (cx - t.x) / t.scale;
      const imgPtY = (cy - t.y) / t.scale;
      const clamped = clampToCover(
        cx - imgPtX * nextScale,
        cy - imgPtY * nextScale,
        photoImg.width,
        photoImg.height,
        nextScale,
        frame,
      );
      onChange({ ...value, transform: { scale: nextScale, ...clamped } });
    },
    [value, onChange, photoImg, template.photoFrame],
  );

  const baseScale = useMemo(() => {
    if (!photoImg) return 1;
    return coverTransform(photoImg.width, photoImg.height, template.photoFrame).scale;
  }, [photoImg, template.photoFrame]);

  /** Zoom runs from cover-fit (0) to 3x cover (1). */
  const ZOOM_RANGE = 2; // max = baseScale * (1 + ZOOM_RANGE)
  const scaleAtPosition = (position: number) => baseScale * (1 + position * ZOOM_RANGE);
  const zoomPosition = value.transform
    ? clamp((value.transform.scale / baseScale - 1) / ZOOM_RANGE, 0, 1)
    : 0;

  // Render the stage at exactly 2x the logical canvas, regardless of the
  // on-screen fit-scale, then restore the display transform.
  const renderAt2x = useCallback(
    <T,>(draw: (stage: Konva.Stage) => T): T | null => {
      const stage = stageRef.current;
      if (!stage) return null;
      stage.scale({ x: 1, y: 1 });
      stage.size({ width: canvas.width, height: canvas.height });
      const out = draw(stage);
      stage.scale({ x: fit, y: fit });
      stage.size({ width: canvas.width * fit, height: canvas.height * fit });
      return out;
    },
    [fit, canvas.width, canvas.height],
  );

  useImperativeHandle(
    ref,
    () => ({
      toDataURL: (format: ExportFormat = 'png') =>
        renderAt2x((stage) =>
          stage.toDataURL({ pixelRatio: 2, mimeType: exportMime(format), quality: exportQuality(format) }),
        ),
      toBlob: (format: ExportFormat = 'png') =>
        new Promise<Blob | null>((resolve) => {
          const done = renderAt2x((stage) =>
            stage
              .toCanvas({ pixelRatio: 2 })
              .toBlob((b: Blob | null) => resolve(b), exportMime(format), exportQuality(format)),
          );
          if (done === null) resolve(null);
        }),
    }),
    [renderAt2x],
  );

  const { photoFrame: pf, namePanel: np, nameText: nt } = template;
  const sn = template.showName;
  const name = value.artistName || 'artist name';
  const showName = value.showName || 'show name';

  // Fit each label into its fixed panel (wrap first, shrink only if needed).
  // fontReady is a dep so it re-measures once the real font loads.
  const fittedFontSize = useMemo(() => {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return nt.fontSize;
    return fitFontSize(ctx, name, nt.box, nt.fontSize, nt.fontFamily);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, nt.fontSize, nt.fontFamily, nt.box.width, nt.box.height, fontReady]);

  const fittedShowFontSize = useMemo(() => {
    if (!sn) return 0;
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return sn.fontSize;
    return fitFontSize(ctx, showName, sn.box, sn.fontSize, sn.fontFamily);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showName, sn?.fontSize, sn?.fontFamily, sn?.box.width, sn?.box.height, fontReady]);

  return (
    <div ref={containerRef} className={className}>
      <Stage
        ref={stageRef}
        width={canvas.width * fit}
        height={canvas.height * fit}
        scaleX={fit}
        scaleY={fit}
        style={{ borderRadius: 8, overflow: 'hidden' }}
      >
        <Layer>
          {/* 1. Back plate: navy + halftone + border */}
          {backImg && (
            <KonvaImage
              image={backImg}
              width={canvas.width}
              height={canvas.height}
              listening={false}
            />
          )}

          {/* 2. Artist photo, clipped to the frame */}
          <Group
            clipX={pf.x}
            clipY={pf.y}
            clipWidth={pf.width}
            clipHeight={pf.height}
          >
            {photoImg && value.transform ? (
              <KonvaImage
                image={photoImg}
                x={value.transform.x}
                y={value.transform.y}
                scaleX={value.transform.scale}
                scaleY={value.transform.scale}
                draggable
                onDragEnd={handleDragEnd}
                // Constrain the drag (in absolute/scaled coords) so the image
                // always fully covers the frame — no gaps inside the photo.
                dragBoundFunc={(pos) => {
                  const s = value.transform!.scale;
                  const local = clampToCover(
                    pos.x / fit,
                    pos.y / fit,
                    photoImg.width,
                    photoImg.height,
                    s,
                    pf,
                  );
                  return { x: local.x * fit, y: local.y * fit };
                }}
              />
            ) : (
              <Text
                x={pf.x}
                y={pf.y + pf.height / 2 - 20}
                width={pf.width}
                text="Drop a photo here"
                fontFamily="sans-serif"
                fontSize={34}
                fill="#ffffff"
                opacity={0.4}
                align="center"
                listening={false}
              />
            )}
          </Group>

          {/* 3. Orange photo frame, on top of the photo (Figma stroke align INSIDE:
               inset the path by half the width so the stroke stays inside the frame). */}
          <Rect
            x={pf.x + template.border.width / 2}
            y={pf.y + template.border.width / 2}
            width={pf.width - template.border.width}
            height={pf.height - template.border.width}
            stroke={template.border.color}
            strokeWidth={template.border.width}
            listening={false}
          />

          {/* 4. Logo furniture (drawn over the photo) */}
          {logoImg && (
            <KonvaImage
              image={logoImg}
              x={template.assets.logoRect.x}
              y={template.assets.logoRect.y}
              width={template.assets.logoRect.width}
              height={template.assets.logoRect.height}
              listening={false}
            />
          )}

          {/* 5. Name panel: connector + navy box + border */}
          <Line
            points={[np.connector.x, np.connector.y, np.connector.x, np.connector.y + np.connector.length]}
            stroke={np.stroke}
            strokeWidth={np.strokeWidth}
            listening={false}
          />
          <Rect
            x={np.box.x}
            y={np.box.y}
            width={np.box.width}
            height={np.box.height}
            fill={np.fill}
            stroke={np.stroke}
            strokeWidth={np.strokeWidth}
            listening={false}
          />

          {/* 6. Artist name (live text) */}
          <Text
            // fontReady is read so the node re-renders once the custom font loads.
            key={fontReady ? 'font-ready' : 'font-loading'}
            x={nt.box.x}
            y={nt.box.y}
            width={nt.box.width}
            height={nt.box.height}
            text={name}
            fontFamily={nt.fontFamily}
            fontSize={fittedFontSize}
            fill={nt.color}
            align={nt.align}
            verticalAlign="middle"
            letterSpacing={nt.letterSpacing}
            lineHeight={NAME_LINE_HEIGHT}
            wrap="word"
            listening={false}
          />

          {/* 7. Resident only: show/programme name banner (top) */}
          {sn && (
            <>
              <Rect
                x={sn.box.x}
                y={sn.box.y}
                width={sn.box.width}
                height={sn.box.height}
                fill={sn.fill}
                stroke={sn.stroke}
                strokeWidth={sn.strokeWidth}
                listening={false}
              />
              <Text
                key={fontReady ? 'show-ready' : 'show-loading'}
                x={sn.box.x}
                y={sn.box.y}
                width={sn.box.width}
                height={sn.box.height}
                text={showName}
                fontFamily={sn.fontFamily}
                fontSize={fittedShowFontSize}
                fill={sn.color}
                align={sn.align}
                verticalAlign="middle"
                letterSpacing={sn.letterSpacing}
                lineHeight={NAME_LINE_HEIGHT}
                wrap="word"
                listening={false}
              />
            </>
          )}
        </Layer>
      </Stage>

      {/* Zoom control (only meaningful once a photo is placed).

          The slider runs 0..1 and is mapped to a scale, rather than carrying the
          scale directly: cover-fit is an arbitrary float (e.g. 0.537655860349127),
          and a min/step grid anchored there makes the browser's native range
          validation reject values that are a rounding error off the grid. That
          matters because this editor is rendered inside the artist-facing show
          form, where such a violation silently blocks submit. */}
      {photoImg && value.transform && (
        <div className="mt-3 flex items-center gap-3">
          <span className="text-xs text-muted-foreground">Zoom</span>
          <input
            type="range"
            min={0}
            max={1}
            step="any"
            value={zoomPosition}
            onChange={(e) => handleZoom(scaleAtPosition(parseFloat(e.target.value)))}
            className="flex-1"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              onChange({
                ...value,
                transform: coverTransform(photoImg.width, photoImg.height, template.photoFrame),
              })
            }
          >
            Reset
          </Button>
        </div>
      )}
    </div>
  );
});
